import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Key } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const KeywordResponsesManager = () => {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [formData, setFormData] = useState({
    keywords: [],
    response: '',
    category: 'general',
    priority: 0
  });

  useEffect(() => {
    fetchKeywords();
  }, []);

  const fetchKeywords = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/admin/keyword-responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeywords(response.data);
    } catch (error) {
      console.error('Failed to fetch keywords:', error);
      alert('Failed to load keyword responses');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (keyword) => {
    setEditing(keyword._id);
    setFormData({
      keywords: keyword.keywords,
      response: keyword.response,
      category: keyword.category,
      priority: keyword.priority
    });
  };

  const handleSave = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API_URL}/api/admin/keyword-responses/${id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditing(null);
      fetchKeywords();
      alert('Keyword response updated successfully!');
    } catch (error) {
      console.error('Failed to update:', error);
      alert('Failed to update keyword response');
    }
  };

  const handleCreate = async () => {
    if (formData.keywords.length === 0 || !formData.response) {
      alert('Please provide keywords and response');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${API_URL}/api/admin/keyword-responses`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdding(false);
      setFormData({ keywords: [], response: '', category: 'general', priority: 0 });
      fetchKeywords();
      alert('Keyword response created successfully!');
    } catch (error) {
      console.error('Failed to create:', error);
      alert('Failed to create keyword response');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this keyword response?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/admin/keyword-responses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchKeywords();
      alert('Keyword response deleted successfully!');
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Failed to delete keyword response');
    }
  };

  const handleAddKeyword = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const newKeyword = e.target.value.trim().toLowerCase();
      if (!formData.keywords.includes(newKeyword)) {
        setFormData(prev => ({
          ...prev,
          keywords: [...prev.keywords, newKeyword]
        }));
      }
      e.target.value = '';
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Keyword Responses</h2>
          <p className="text-gray-400 text-sm">
            Configure automatic responses based on user input keywords
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors"
        >
          <Plus size={20} />
          Add New
        </button>
      </div>

      {/* Add New Form */}
      {adding && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4">Create New Keyword Response</h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-white font-medium mb-2 block">Keywords</label>
              <input
                type="text"
                onKeyDown={handleAddKeyword}
                placeholder="Type keyword and press Enter"
                className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-yellow-500 outline-none"
              />
              <p className="text-gray-400 text-xs mt-1">Press Enter to add each keyword</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.keywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {keyword}
                    <button
                      onClick={() => handleRemoveKeyword(keyword)}
                      className="hover:bg-black hover:bg-opacity-20 rounded-full p-1"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Response</label>
              <textarea
                value={formData.response}
                onChange={(e) => setFormData(prev => ({ ...prev, response: e.target.value }))}
                placeholder="Enter the response message"
                rows={4}
                className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-yellow-500 outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white font-medium mb-2 block">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., pricing, booking"
                  className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-yellow-500 outline-none"
                />
              </div>
              <div>
                <label className="text-white font-medium mb-2 block">Priority</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  placeholder="0-10"
                  className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-yellow-500 outline-none"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setAdding(false);
                  setFormData({ keywords: [], response: '', category: 'general', priority: 0 });
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keywords List */}
      <div className="space-y-4">
        {keywords.map((keyword) => (
          <div
            key={keyword._id}
            className="bg-gray-800 rounded-lg p-6 border border-gray-700"
          >
            {editing === keyword._id ? (
              <div className="space-y-4">
                <div>
                  <label className="text-white font-medium mb-2 block">Keywords</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.keywords.map((kw, idx) => (
                      <span
                        key={idx}
                        className="bg-yellow-500 text-black px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      >
                        {kw}
                        <button
                          onClick={() => handleRemoveKeyword(kw)}
                          className="hover:bg-black hover:bg-opacity-20 rounded-full p-1"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    onKeyDown={handleAddKeyword}
                    placeholder="Add more keywords (press Enter)"
                    className="w-full bg-gray-700 text-white p-2 rounded-lg border border-gray-600 focus:border-yellow-500 outline-none"
                  />
                </div>

                <div>
                  <label className="text-white font-medium mb-2 block">Response</label>
                  <textarea
                    value={formData.response}
                    onChange={(e) => setFormData(prev => ({ ...prev, response: e.target.value }))}
                    rows={4}
                    className="w-full bg-gray-700 text-white p-3 rounded-lg border border-gray-600 focus:border-yellow-500 outline-none"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(keyword._id)}
                    className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors"
                  >
                    <Save size={16} />
                    Save
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 text-xs rounded bg-blue-500 text-white">
                        {keyword.category}
                      </span>
                      <span className="px-2 py-1 text-xs rounded bg-gray-700 text-white">
                        Priority: {keyword.priority}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {keyword.keywords.map((kw, idx) => (
                        <span
                          key={idx}
                          className="bg-gray-700 text-yellow-500 px-3 py-1 rounded-full text-sm flex items-center gap-1"
                        >
                          <Key size={12} />
                          {kw}
                        </span>
                      ))}
                    </div>
                    <p className="text-white whitespace-pre-wrap">{keyword.response}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(keyword)}
                      className="p-2 text-yellow-500 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(keyword._id)}
                      className="p-2 text-red-500 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {keywords.length === 0 && !adding && (
        <div className="text-center py-12 text-gray-400">
          <Key size={48} className="mx-auto mb-4 opacity-50" />
          <p>No keyword responses yet. Click "Add New" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default KeywordResponsesManager;
